import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
    PERFORMANCE_SUMMARY_PAGE_SIZE,
    type PerformanceSummaryDrilldownFilter,
    type PerformanceSummaryOrderByItem,
} from './usePerformanceSummaryKpis';
import { usePpaQueryFilters } from 'hooks/ppa/usePpaQueryFilters';
import { useKpiDeltaNetwork } from './useKpiDeltaNetwork';
import type {
    KpiDeltaPage,
    PerformanceSummaryFeatureMeta,
    PerformanceSummaryUserInput,
} from './performanceSummaryKpisWithDeltaHelpers';

type UsePerformanceSummaryKpisWithDeltaParams = {
    /** Stable identifier for the consumer scope; goes into the bulk signature. */
    scope: string;
    /** All currently selected KPIs (full visible column set). */
    kpiIds: string[];
    /**
     * KPI used as `kpi_ids[0]` on every backend request. The backend pages by anchor,
     * so a delta call with `[anchorKpiId, newKpi]` returns the same row set as the
     * original bulk fetch — required for client-side merging.
     */
    anchorKpiId: string;
    dimension?: string;
    aggregateAttributeTypes: string[];
    aggregateDimensions?: Array<string | undefined>;
    aggregateIsAttributes?: Array<boolean | undefined>;
    drilldownFilters?: PerformanceSummaryDrilldownFilter[];
    orderBy?: PerformanceSummaryOrderByItem[] | null;
    enabled: boolean;
    normalizeData?: (rows: unknown[]) => unknown[];
    pageSize?: number;
    userInputs?: PerformanceSummaryUserInput[];
    featureMeta?: PerformanceSummaryFeatureMeta;
};

type Result<TNode> = {
    data: { pages: KpiDeltaPage<TNode>[] } | undefined;
    isLoading: boolean;
    isFetching: boolean;
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    fetchNextPage: () => void;
    /**
     * KPI ids that have been added to the selection but whose data is not yet
     * present in the cache. Use these for per-column spinners.
     */
    pendingKpiIds: string[];
};

const usePerformanceSummaryKpisWithDelta = <TNode>({
    scope,
    kpiIds,
    anchorKpiId,
    dimension,
    aggregateAttributeTypes,
    aggregateDimensions,
    aggregateIsAttributes,
    drilldownFilters,
    orderBy,
    enabled,
    normalizeData,
    pageSize = PERFORMANCE_SUMMARY_PAGE_SIZE,
    userInputs,
    featureMeta,
}: UsePerformanceSummaryKpisWithDeltaParams): Result<TNode> => {
    const {
        dim1_id_list,
        dim2_id_list,
        hasResolvedFilterIds,
        startTimestamp,
        endTimestamp,
        hasValidTimePeriod,
        filtersLoading,
        globalFilterQueryKey,
    } = usePpaQueryFilters();

    const dim1QueryKey = JSON.stringify(dim1_id_list ?? []);
    const dim2QueryKey = JSON.stringify(dim2_id_list ?? []);

    const timePeriod = useMemo(
        () => ({
            start_timestamp: startTimestamp,
            end_timestamp: endTimestamp,
        }),
        [startTimestamp, endTimestamp],
    );
    const filters = useMemo(
        () => ({ dim1_id_list, dim2_id_list }),
        [dim1_id_list, dim2_id_list],
    );

    const canFetchKpis =
        enabled &&
        !filtersLoading &&
        hasResolvedFilterIds &&
        hasValidTimePeriod;

    const bulkSignature = useMemo(
        () =>
            JSON.stringify([
                scope,
                anchorKpiId,
                dimension ?? '',
                aggregateAttributeTypes,
                aggregateDimensions ?? [],
                aggregateIsAttributes ?? [],
                drilldownFilters ?? [],
                orderBy ?? [],
                startTimestamp,
                endTimestamp,
                dim1QueryKey,
                dim2QueryKey,
                globalFilterQueryKey,
                userInputs ?? [],
                featureMeta ?? {},
                pageSize,
            ]),
        [
            scope,
            anchorKpiId,
            dimension,
            aggregateAttributeTypes,
            aggregateDimensions,
            aggregateIsAttributes,
            drilldownFilters,
            orderBy,
            startTimestamp,
            endTimestamp,
            dim1QueryKey,
            dim2QueryKey,
            globalFilterQueryKey,
            userInputs,
            featureMeta,
            pageSize,
        ],
    );

    const lastBulkSignatureRef = useRef<string | null>(null);

    const {
        pages,
        pagesRef,
        knownKpiIds,
        knownKpiIdsRef,
        inFlightKpiIdsRef,
        generationRef,
        isFetchingNextPage,
        activeFetchCount,
        runBulk,
        runDelta,
    } = useKpiDeltaNetwork<TNode>({
        anchorKpiId,
        dimension,
        aggregateAttributeTypes,
        aggregateDimensions,
        aggregateIsAttributes,
        drilldownFilters,
        orderBy,
        pageSize,
        timePeriod,
        filters,
        userInputs,
        featureMeta,
        normalizeData,
    });

    useEffect(() => {
        if (!canFetchKpis) return;
        if (lastBulkSignatureRef.current === bulkSignature) return;
        lastBulkSignatureRef.current = bulkSignature;
        generationRef.current += 1;
        inFlightKpiIdsRef.current = new Set();
        if (kpiIds.length > 0) void runBulk(kpiIds, 0, 'replace');
    }, [canFetchKpis, bulkSignature, kpiIds, runBulk, generationRef, inFlightKpiIdsRef]);

    useEffect(() => {
        if (!canFetchKpis) return;
        if (lastBulkSignatureRef.current !== bulkSignature) return;
        if (!pagesRef.current.length) return;
        const known = knownKpiIdsRef.current;
        const inFlight = inFlightKpiIdsRef.current;
        const addedKpiIds = kpiIds.filter((id) => !known.has(id) && !inFlight.has(id));
        if (!addedKpiIds.length) return;
        void runDelta(addedKpiIds);
    }, [
        canFetchKpis,
        bulkSignature,
        kpiIds,
        pages,
        runDelta,
        pagesRef,
        knownKpiIdsRef,
        inFlightKpiIdsRef,
    ]);

    const fetchNextPage = useCallback(() => {
        if (!canFetchKpis) return;
        if (isFetchingNextPage) return;
        const lastPage = pagesRef.current[pagesRef.current.length - 1];
        if (!lastPage) return;
        const total = pagesRef.current[0]?.total;
        const nextOffset = lastPage.offset + lastPage.limit;
        if (total !== undefined ? nextOffset >= total : lastPage.nodes.length < pageSize) return;
        void runBulk(kpiIds, nextOffset, 'append');
    }, [canFetchKpis, isFetchingNextPage, kpiIds, pageSize, runBulk, pagesRef]);

    const hasNextPage = useMemo(() => {
        const lastPage = pages[pages.length - 1];
        if (!lastPage) return false;
        const total = pages[0]?.total;
        const nextOffset = lastPage.offset + lastPage.limit;
        if (total !== undefined) return nextOffset < total;
        return lastPage.nodes.length >= pageSize;
    }, [pages, pageSize]);

    const pendingKpiIds = useMemo(() => {
        if (pages.length === 0) return [];
        return kpiIds.filter((id) => !knownKpiIds.has(id));
    }, [kpiIds, knownKpiIds, pages.length]);

    const data = pages.length ? { pages } : undefined;
    const isFetching = activeFetchCount > 0;
    const isLoading = pages.length === 0 && isFetching;

    return {
        data,
        isLoading,
        isFetching,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        pendingKpiIds,
    };
};

export default usePerformanceSummaryKpisWithDelta;
